'use strict'

/**
 * SwatchPanelDataBuilder
 *
 * Pure helpers to convert swatch datasets into the normalized structures
 * expected by the UI adapters (color pages, texture pages, texture-swatch pages,
 * and search result pages).
 *
 * These helpers are intentionally stateless and UI-agnostic.
 */

/**
 * Normalize a color page into a fixed-length array of slots.
 * Each slot contains the color object or null.
 *
 * @param {Object} swatchPage
 * @returns {Array<Object|null>}
 */
const buildColorSlots = (swatchPage) => {
    const count = swatchPage?.number_of_colors || 0
    const slots = new Array(count).fill(null)
    const colors = swatchPage?.swatch_colors || []

    colors.forEach((color) => {
        const pos = typeof color.position === 'number' ? color.position : null
        if (pos !== null && pos >= 0 && pos < count) {
            slots[pos] = color
        }
    })

    return slots
}

/**
 * Build paged rows for flat search results.
 *
 * @param {Array<Object>} results
 * @param {number} perRow
 * @param {(row:Object, index:number) => (Object|null)} slotBuilder
 * @returns {Array<Object>}
 */
const buildSearchPages = (results, perRow, slotBuilder) => {
    const pages = []
    const slotsPerRow = Math.max(1, perRow || 1)

    for (let i = 0; i < results.length; i += slotsPerRow) {
        const slice = results.slice(i, i + slotsPerRow)
        const slots = new Array(slotsPerRow).fill(null)

        slice.forEach((row, index) => {
            slots[index] = slotBuilder(row, index)
        })

        pages.push({
            id: `search-${pages.length + 1}`,
            pageName: '',
            numberOfSlots: slotsPerRow,
            slots,
        })
    }

    return pages
}

/**
 * Build adapter-ready page data for color swatches.
 *
 * @param {Object} swatch - Current swatch dataset.
 * @returns {Array<Object>}
 */
export const buildColorPagesData = (swatch) => {
    const pages = swatch?.swatch_pages || []

    return pages.map((page) => {
        const slots = buildColorSlots(page)

        return {
            id: page.id ?? page.page_number,
            pageName: page.page_name,
            numberOfSlots: slots.length,
            slots: slots.map((colorOrNull) => {
                if (!colorOrNull) return null

                return {
                    id: colorOrNull.id,
                    name: colorOrNull.name,
                    label: colorOrNull.name,
                    position: colorOrNull.position,
                    swatchId: colorOrNull.swatch_id ?? swatch?.id,
                    rgb: {
                        r: colorOrNull.sr,
                        g: colorOrNull.sg,
                        b: colorOrNull.sb,
                    },
                    colorFilename: colorOrNull.color_filename,
                }
            }),
        }
    })
}

/**
 * Build adapter-ready page data for texture swatches
 * when the current swatch contains color_filename (texture tiles).
 *
 * @param {Object} swatch - Current swatch dataset.
 * @returns {Array<Object>}
 */
export const buildTexturePagesData = (swatch) => {
    const pages = swatch?.swatch_pages || []
    const swatchId = swatch?.id

    return pages.map((page) => {
        const slots = buildColorSlots(page)

        return {
            id: page.id ?? page.page_number,
            pageName: page.page_name,
            numberOfSlots: slots.length,
            slots: slots.map((colorOrNull) => {
                if (!colorOrNull) return null

                const imagePath = swatchId ? `/swatches/${swatchId}/color/${colorOrNull.color_filename}` : null

                return {
                    id: colorOrNull.id,
                    name: colorOrNull.name,
                    label: colorOrNull.name,
                    position: colorOrNull.position,
                    swatchId: colorOrNull.swatch_id ?? swatchId,
                    imagePath,
                }
            }),
        }
    })
}

/**
 * Build adapter-ready page data for texture-swatch datasets.
 *
 * @param {Object} textureSwatch - Texture-swatch dataset.
 * @returns {Array<Object>}
 */
export const buildTextureSwatchPagesData = (textureSwatch) => {
    const pages = textureSwatch?.texture_pages || []
    const swatchId = textureSwatch?.id

    return pages.map((page) => {
        const textures = page.textures || []
        const slotCount = textures.length || 0
        const slots = new Array(slotCount).fill(null)

        textures.forEach((tex) => {
            const pos = typeof tex.position === 'number' ? tex.position : null
            if (pos !== null && pos >= 0 && pos < slotCount) {
                slots[pos] = tex
            }
        })

        return {
            id: page.id ?? page.page_number,
            pageName: page.name,
            numberOfSlots: slots.length,
            slots: slots.map((texOrNull) => {
                if (!texOrNull) return null

                const imagePath = swatchId ? `/texture-swatches/${swatchId}/image/${texOrNull.image_filename}` : null

                return {
                    id: texOrNull.id,
                    name: texOrNull.name,
                    label: texOrNull.name,
                    position: texOrNull.position,
                    textureSwatchId: swatchId,
                    imagePath,
                }
            }),
        }
    })
}

/**
 * Build adapter-ready page data for swatch color search results.
 *
 * @param {Array<Object>} results
 * @param {number} perRow
 * @returns {Array<Object>}
 */
export const buildSwatchSearchPagesData = (results, perRow) => {
    return buildSearchPages(results, perRow, (row, index) => ({
        id: row.color.id,
        name: row.color.name,
        label: row.color.name,
        position: index,
        swatchId: row.swatchId,
        rgb: {
            r: row.color.sr,
            g: row.color.sg,
            b: row.color.sb,
        },
        colorFilename: row.color.color_filename,
    }))
}

/**
 * Build adapter-ready page data for texture search results.
 *
 * @param {Array<Object>} results
 * @param {number} perRow
 * @returns {Array<Object>}
 */
export const buildTextureSearchPagesData = (results, perRow) => {
    return buildSearchPages(results, perRow, (row, index) => ({
        id: row.color.id,
        name: row.color.name,
        label: row.color.name,
        position: index,
        swatchId: row.swatchId,
        imagePath: `/swatches/${row.swatchId}/color/${row.color.color_filename}`,
    }))
}

/**
 * Build adapter-ready page data for texture-swatch search results.
 * Results are flattened, so this helper paginates them into fixed-size rows.
 *
 * @param {Array<Object>} results
 * @param {number} perRow
 * @returns {Array<Object>}
 */
export const buildTextureSwatchSearchPagesData = (results, perRow) => {
    return buildSearchPages(results, perRow, (row, index) => ({
        id: row.texture.id,
        name: row.texture.name,
        label: row.texture.name,
        position: index,
        textureSwatchId: row.textureSwatchId,
        imagePath: `/texture-swatches/${row.textureSwatchId}/image/${row.texture.image_filename}`,
    }))
}
